import { useCallback, useEffect, useMemo, useState } from "react";
import { ClientApiError } from "../auth/api";
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Skeleton,
  Table,
  Textarea,
  Toast,
} from "../components/ui";
import {
  addAdminTaxonomyAlias,
  bulkAdminTaxonomyStatus,
  createAdminTaxonomyItem,
  deleteAdminTaxonomyItem,
  getAdminTaxonomyItem,
  getAdminTaxonomyItems,
  getAdminTaxonomyNamespaces,
  patchAdminTaxonomyItem,
  removeAdminTaxonomyAlias,
  reorderAdminTaxonomy,
  type TaxonomyItemView,
  type TaxonomyNamespaceView,
} from "../admin/taxonomy-api";

interface EditorState {
  item: TaxonomyItemView | null;
  code: string;
  slug: string;
  nameVi: string;
  nameEn: string;
  parentId: string;
  status: string;
  sortOrder: string;
  sourceNote: string;
  metadataJson: string;
}

function emptyEditor(): EditorState {
  return {
    item: null,
    code: "",
    slug: "",
    nameVi: "",
    nameEn: "",
    parentId: "",
    status: "active",
    sortOrder: "0",
    sourceNote: "",
    metadataJson: "{}",
  };
}

function editorFromItem(item: TaxonomyItemView): EditorState {
  return {
    item,
    code: item.code,
    slug: item.slug,
    nameVi: item.nameVi,
    nameEn: item.nameEn ?? "",
    parentId: item.parentId ?? "",
    status: item.status,
    sortOrder: String(item.sortOrder),
    sourceNote: item.sourceNote ?? "",
    metadataJson: JSON.stringify(item.metadata, null, 2),
  };
}

function statusTone(status: string): "success" | "warning" | "neutral" {
  if (status === "active") return "success";
  if (status === "deprecated") return "warning";
  return "neutral";
}

export function AdminTaxonomyPage() {
  const [namespaces, setNamespaces] = useState<TaxonomyNamespaceView[]>([]);
  const [namespace, setNamespace] = useState("breed");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState<TaxonomyItemView[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [references, setReferences] = useState<number | null>(null);
  const [aliasValue, setAliasValue] = useState("");
  const [aliasLocale, setAliasLocale] = useState("vi-VN");
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);

  const loadNamespaces = useCallback(async () => {
    try {
      const response = await getAdminTaxonomyNamespaces();
      setNamespaces(response.namespaces);
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError(caught.problem.message);
        setRequestId(caught.problem.requestId);
      } else {
        setError("Không thể tải nhóm taxonomy.");
      }
    }
  }, []);

  const loadItems = useCallback(
    async (cursor: string | null = null, append = false) => {
      append ? setLoadingMore(true) : setLoading(true);
      setError(null);
      try {
        const response = await getAdminTaxonomyItems({
          namespace,
          query: query.trim(),
          status,
          cursor,
          limit: 25,
        });
        setItems((current) => (append ? [...current, ...response.items] : response.items));
        setNextCursor(response.nextCursor);
        setTotal(response.total);
        setRequestId(response.requestId);
        if (!append) setSelectedIds(new Set());
      } catch (caught) {
        if (caught instanceof ClientApiError) {
          setError(caught.problem.message);
          setRequestId(caught.problem.requestId);
        } else {
          setError("Không thể tải taxonomy.");
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [namespace, query, status],
  );

  useEffect(() => {
    void loadNamespaces();
  }, [loadNamespaces]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadItems(), 250);
    return () => window.clearTimeout(timer);
  }, [loadItems]);

  const selectedCount = selectedIds.size;
  const currentNamespace = useMemo(
    () => namespaces.find((entry) => entry.code === namespace) ?? null,
    [namespace, namespaces],
  );

  function toggleSelected(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function openEditor(item?: TaxonomyItemView) {
    setReferences(null);
    setAliasValue("");
    setEditor(item ? editorFromItem(item) : emptyEditor());
    if (item) {
      try {
        const response = await getAdminTaxonomyItem(item.id);
        setEditor(editorFromItem(response.item));
        setReferences(response.references);
      } catch (caught) {
        if (caught instanceof ClientApiError) {
          setError(caught.problem.message);
          setRequestId(caught.problem.requestId);
        }
      }
    }
  }

  function updateEditor(patch: Partial<EditorState>) {
    setEditor((current) => (current ? { ...current, ...patch } : current));
  }

  async function saveEditor() {
    if (!editor) return;
    setSaving(true);
    setError(null);
    try {
      let metadata: Record<string, unknown>;
      try {
        const parsed = JSON.parse(editor.metadataJson) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("invalid");
        metadata = parsed as Record<string, unknown>;
      } catch {
        throw new Error("Metadata phải là object JSON hợp lệ.");
      }
      const body = {
        code: editor.code,
        slug: editor.slug,
        nameVi: editor.nameVi,
        nameEn: editor.nameEn || null,
        parentId: editor.parentId || null,
        status: editor.status,
        sortOrder: Number(editor.sortOrder),
        sourceNote: editor.sourceNote || null,
        metadata,
      };
      const response = editor.item
        ? await patchAdminTaxonomyItem(editor.item.id, {
            ...body,
            expectedVersion: editor.item.version,
          })
        : await createAdminTaxonomyItem({ namespace, ...body });
      setToast({
        title: editor.item ? "Đã cập nhật" : "Đã tạo taxonomy",
        message: response.item.nameVi,
      });
      setEditor(editorFromItem(response.item));
      await loadItems();
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError(caught.problem.message);
        setRequestId(caught.problem.requestId);
      } else {
        setError(caught instanceof Error ? caught.message : "Không thể lưu taxonomy.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function addAlias() {
    if (!editor?.item || !aliasValue.trim()) return;
    setSaving(true);
    try {
      const response = await addAdminTaxonomyAlias(editor.item.id, {
        alias: aliasValue,
        locale: aliasLocale,
      });
      setEditor(editorFromItem(response.item));
      setAliasValue("");
      setToast({ title: "Đã thêm alias", message: "Autocomplete có thể tìm theo alias mới." });
      await loadItems();
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError(caught.problem.message);
        setRequestId(caught.problem.requestId);
      }
    } finally {
      setSaving(false);
    }
  }

  async function removeAlias(aliasId: string) {
    if (!editor?.item) return;
    setSaving(true);
    try {
      const response = await removeAdminTaxonomyAlias(editor.item.id, aliasId);
      setEditor(editorFromItem(response.item));
      setToast({ title: "Đã xóa alias", message: "Chỉ alias được xóa; item chính vẫn giữ nguyên." });
      await loadItems();
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError(caught.problem.message);
        setRequestId(caught.problem.requestId);
      }
    } finally {
      setSaving(false);
    }
  }

  async function applyBulk(nextStatus: string) {
    if (selectedCount === 0) return;
    setSaving(true);
    try {
      await bulkAdminTaxonomyStatus([...selectedIds], nextStatus);
      setToast({ title: "Đã cập nhật hàng loạt", message: `${selectedCount} item chuyển sang ${nextStatus}.` });
      await loadItems();
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError(caught.problem.message);
        setRequestId(caught.problem.requestId);
      }
    } finally {
      setSaving(false);
    }
  }

  async function moveItem(item: TaxonomyItemView, delta: number) {
    setSaving(true);
    try {
      await reorderAdminTaxonomy([
        { id: item.id, sortOrder: item.sortOrder + delta, expectedVersion: item.version },
      ]);
      await loadItems();
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError(caught.problem.message);
        setRequestId(caught.problem.requestId);
      }
    } finally {
      setSaving(false);
    }
  }

  async function deleteCurrent() {
    if (!editor?.item) return;
    setSaving(true);
    try {
      await deleteAdminTaxonomyItem(editor.item.id);
      setEditor(null);
      setToast({ title: "Đã xóa taxonomy", message: "Item không có tham chiếu đã được xóa vật lý." });
      await loadItems();
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError(caught.problem.message);
        setRequestId(caught.problem.requestId);
      }
    } finally {
      setSaving(false);
    }
  }

  const rows = items.map((item) => [
    <button
      key={`select-${item.id}`}
      type="button"
      className="taxonomy-select-button"
      aria-pressed={selectedIds.has(item.id)}
      aria-label={`${selectedIds.has(item.id) ? "Bỏ chọn" : "Chọn"} ${item.nameVi}`}
      onClick={() => toggleSelected(item.id)}
    >
      {selectedIds.has(item.id) ? "Đã chọn" : "Chọn"}
    </button>,
    <div key={`name-${item.id}`} className="taxonomy-name-cell">
      <strong>{item.nameVi}</strong>
      <small>{item.nameEn ?? item.slug}</small>
      {item.aliases.length > 0 ? <span>{item.aliases.slice(0, 2).map((alias) => alias.value).join(" · ")}</span> : null}
    </div>,
    <code key={`code-${item.id}`}>{item.code}</code>,
    <Badge key={`status-${item.id}`} tone={statusTone(item.status)}>{item.status}</Badge>,
    <span key={`sort-${item.id}`}>{item.sortOrder}</span>,
    <div key={`actions-${item.id}`} className="table-actions">
      <Button variant="ghost" size="sm" disabled={saving} onClick={() => void moveItem(item, -10)}>
        Lên
      </Button>
      <Button variant="ghost" size="sm" disabled={saving} onClick={() => void moveItem(item, 10)}>
        Xuống
      </Button>
      <Button variant="secondary" size="sm" onClick={() => void openEditor(item)}>
        Sửa
      </Button>
    </div>,
  ]);

  return (
    <section className="page-stack taxonomy-admin-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Master data</p>
          <h2>Giống và taxonomy</h2>
          <p>Catalog versioned cho mọi form chó, kennel, health test và thành tích.</p>
        </div>
        <Button onClick={() => void openEditor()}>Thêm item</Button>
      </header>

      <section className="content-card taxonomy-toolbar" aria-label="Bộ lọc taxonomy">
        <Select
          label="Nhóm dữ liệu"
          value={namespace}
          onChange={(event) => setNamespace(event.target.value)}
        >
          {namespaces.map((entry) => (
            <option key={entry.code} value={entry.code}>{entry.nameVi}</option>
          ))}
        </Select>
        <Input
          label="Tìm kiếm"
          type="search"
          placeholder="Tên, code, slug hoặc alias"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Select label="Trạng thái" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">Tất cả</option>
          <option value="active">Active</option>
          <option value="deprecated">Deprecated</option>
          <option value="archived">Archived</option>
        </Select>
      </section>

      <section className="taxonomy-summary">
        <div>
          <strong>{currentNamespace?.nameVi ?? namespace}</strong>
          <span>{total} item · {selectedCount} đã chọn</span>
        </div>
        <div className="button-row">
          <Button variant="secondary" size="sm" disabled={selectedCount === 0 || saving} onClick={() => void applyBulk("active")}>
            Bật
          </Button>
          <Button variant="secondary" size="sm" disabled={selectedCount === 0 || saving} onClick={() => void applyBulk("deprecated")}>
            Deprecate
          </Button>
          <Button variant="danger" size="sm" disabled={selectedCount === 0 || saving} onClick={() => void applyBulk("archived")}>
            Archive
          </Button>
        </div>
      </section>

      {error ? (
        <ErrorState
          title="Không thể xử lý taxonomy"
          description={error}
          requestId={requestId ?? undefined}
          action={{ label: "Tải lại", onClick: () => void loadItems() }}
        />
      ) : null}

      {loading ? (
        <section className="content-card"><Skeleton lines={7} /></section>
      ) : items.length === 0 ? (
        <EmptyState
          title="Không có taxonomy"
          description="Thay đổi bộ lọc hoặc tạo item đầu tiên trong namespace này."
          action={{ label: "Thêm item", onClick: () => void openEditor() }}
        />
      ) : (
        <>
          <Table
            caption={`Danh sách ${currentNamespace?.nameVi ?? namespace}`}
            headers={["Chọn", "Tên", "Code", "Trạng thái", "Thứ tự", "Thao tác"]}
            rows={rows}
          />
          {nextCursor ? (
            <div className="taxonomy-load-more">
              <Button
                variant="secondary"
                disabled={loadingMore}
                onClick={() => void loadItems(nextCursor, true)}
              >
                {loadingMore ? "Đang tải…" : "Tải thêm"}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <Dialog
        open={Boolean(editor)}
        title={editor?.item ? "Sửa taxonomy" : "Thêm taxonomy"}
        onClose={() => setEditor(null)}
      >
        {editor ? (
          <div className="taxonomy-editor">
            <div className="form-grid">
              <Input label="Code" value={editor.code} onChange={(event) => updateEditor({ code: event.target.value })} />
              <Input label="Slug" value={editor.slug} onChange={(event) => updateEditor({ slug: event.target.value })} />
              <Input label="Tên tiếng Việt" value={editor.nameVi} onChange={(event) => updateEditor({ nameVi: event.target.value })} />
              <Input label="Tên tiếng Anh" value={editor.nameEn} onChange={(event) => updateEditor({ nameEn: event.target.value })} />
              <Select label="Trạng thái" value={editor.status} onChange={(event) => updateEditor({ status: event.target.value })}>
                <option value="active">Active</option>
                <option value="deprecated">Deprecated</option>
                <option value="archived">Archived</option>
              </Select>
              <Input label="Sort order" type="number" value={editor.sortOrder} onChange={(event) => updateEditor({ sortOrder: event.target.value })} />
              <Input label="Parent item ID" value={editor.parentId} onChange={(event) => updateEditor({ parentId: event.target.value })} hint="Để trống nếu là item cấp cao nhất." />
              <Textarea label="Chú thích nguồn" value={editor.sourceNote} onChange={(event) => updateEditor({ sourceNote: event.target.value })} />
            </div>
            <Textarea
              label="Metadata JSON"
              value={editor.metadataJson}
              onChange={(event) => updateEditor({ metadataJson: event.target.value })}
              rows={7}
            />

            {editor.item ? (
              <section className="taxonomy-alias-editor">
                <header>
                  <div>
                    <h3>Aliases</h3>
                    <p>Tìm kiếm công khai khớp cả tên chính và alias đã chuẩn hóa.</p>
                  </div>
                  <Badge tone={references && references > 0 ? "warning" : "neutral"}>
                    {references === null ? "Đang kiểm tra tham chiếu" : `${references} tham chiếu`}
                  </Badge>
                </header>
                <div className="taxonomy-alias-list">
                  {editor.item.aliases.length === 0 ? <span>Chưa có alias.</span> : editor.item.aliases.map((alias) => (
                    <article key={alias.id}>
                      <span><strong>{alias.value}</strong><small>{alias.locale}</small></span>
                      <Button variant="ghost" size="sm" disabled={saving} onClick={() => void removeAlias(alias.id)}>Xóa</Button>
                    </article>
                  ))}
                </div>
                <div className="taxonomy-alias-form">
                  <Input label="Alias mới" value={aliasValue} onChange={(event) => setAliasValue(event.target.value)} />
                  <Select label="Ngôn ngữ" value={aliasLocale} onChange={(event) => setAliasLocale(event.target.value)}>
                    <option value="vi-VN">Tiếng Việt</option>
                    <option value="en">English</option>
                    <option value="de">Deutsch</option>
                    <option value="und">Không xác định</option>
                  </Select>
                  <Button variant="secondary" disabled={saving || !aliasValue.trim()} onClick={() => void addAlias()}>Thêm alias</Button>
                </div>
              </section>
            ) : null}

            <div className="taxonomy-editor__actions">
              {editor.item ? (
                <Button
                  variant="danger"
                  disabled={saving || editor.item.seeded || (references ?? 0) > 0}
                  onClick={() => void deleteCurrent()}
                >
                  Xóa vật lý
                </Button>
              ) : <span />}
              <div className="button-row">
                <Button variant="secondary" onClick={() => setEditor(null)}>Hủy</Button>
                <Button disabled={saving} onClick={() => void saveEditor()}>
                  {saving ? "Đang lưu…" : "Lưu thay đổi"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </Dialog>

      {toast ? (
        <Toast
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </section>
  );
}
